import { Fragment, type ReactNode } from 'react';

/**
 * Markdown do manual desenhado com elementos React (nunca HTML cru): títulos, parágrafos,
 * listas, código e ênfases. Links só para http(s), caminhos relativos e âncoras.
 */
export function slug(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;

/** Opções do desenho: `links: 'internal'` aceita só caminhos da própria console e âncoras; `text` desenha o texto puro (ex.: [E1] como link). */
export interface MarkdownOptions {
  links?: 'all' | 'internal';
  text?: (s: string, key: string) => ReactNode;
}

export function safeHref(href: string, links: 'all' | 'internal' = 'all'): string | null {
  if (href.startsWith('#') || (href.startsWith('/') && !href.startsWith('//'))) return href;
  if (links === 'all' && /^https?:\/\//i.test(href)) return href;
  return null;
}

export function inline(text: string, keyBase = 'i', opts: MarkdownOptions = {}): ReactNode[] {
  const out: ReactNode[] = [];
  const plain = (s: string, key: string): ReactNode => (opts.text ? <Fragment key={key}>{opts.text(s, key)}</Fragment> : s);
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const tok = m[0];
    const at = m.index ?? 0;
    if (at > last) out.push(plain(text.slice(last, at), `${keyBase}-t${n}`));
    const key = `${keyBase}-${n++}`;
    if (tok.startsWith('**')) out.push(<strong key={key}>{plain(tok.slice(2, -2), `${key}-s`)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={key} className="mono">{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('[')) {
      const mm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      const href = mm ? safeHref(mm[2] ?? '', opts.links) : null;
      out.push(
        href ? (
          <a key={key} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer noopener">
            {mm?.[1]}
          </a>
        ) : (
          <Fragment key={key}>{mm?.[1] ?? tok}</Fragment>
        ),
      );
    } else out.push(<em key={key}>{plain(tok.slice(1, -1), `${key}-e`)}</em>);
    last = at + tok.length;
  }
  if (last < text.length) out.push(plain(text.slice(last), `${keyBase}-t${n}`));
  return out;
}

export function Markdown({ source, headingOffset = 1, ...opts }: { source: string; headingOffset?: number } & MarkdownOptions) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (!line.trim()) {
      i++;
      continue;
    }
    if (line.startsWith('```')) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? '').startsWith('```')) code.push(lines[i++] ?? '');
      i++;
      blocks.push(
        <pre key={k++} className="well mono small" style={{ overflowX: 'auto', margin: 0 }}>
          <code>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = Math.min(6, (h[1]?.length ?? 1) + headingOffset);
      const text = h[2] ?? '';
      const Tag = `h${level}` as 'h2';
      blocks.push(
        <Tag key={k++} id={slug(text)} style={{ fontSize: level <= 3 ? 15 : 14, marginTop: 6 }}>
          {inline(text, `h${k}`, opts)}
        </Tag>,
      );
      i++;
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^\s*([-*]|\d+\.)\s+/, ''));
        i++;
      }
      const L = ordered ? 'ol' : 'ul';
      blocks.push(
        <L key={k++} style={{ margin: 0, paddingLeft: 20 }} className="stack-sm">
          {items.map((it, j) => (
            <li key={j}>{inline(it, `l${k}-${j}`, opts)}</li>
          ))}
        </L>,
      );
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && (lines[i] ?? '').trim() && !/^(#{1,4}\s|```|\s*([-*]|\d+\.)\s+)/.test(lines[i] ?? '')) para.push((lines[i++] ?? '').trim());
    blocks.push(<p key={k++}>{inline(para.join(' '), `p${k}`, opts)}</p>);
  }
  return <div className="stack-sm md">{blocks}</div>;
}
